import { Component } from '@theme/component';

/**
 * <q-presets> — one-tap chips that fill Horizon's own form fields.
 * Used by the price ranges (snippets/custom-price-presets.liquid) and the
 * phone sort pills (snippets/custom-sort-pills.liquid).
 *
 * Owns no filtering or sorting logic. A tap writes values into the fields
 * Horizon already renders and dispatches the same `change` event a shopper's
 * own input would; Horizon's on:change handler (price facet / sorting
 * component) then updates results, counts, chips and the URL.
 *
 * Markup contract:
 *   <q-presets data-scope="price-facet-component" data-toggle>
 *     <button on:click="/apply" aria-pressed="false"
 *             data-values='{"filter.v.price.lte":"100"}'>…</button>
 *   data-scope   selector of the ancestor that holds the target fields
 *   data-toggle  tapping the pressed chip clears its fields (price ranges);
 *                without it a pressed chip stays pressed (sort: one is
 *                always active)
 *   data-values  JSON: field name → value ("" empties the field)
 */
class Presets extends Component {
  /** @param {Event} event */
  apply(event) {
    const chip = /** @type {HTMLButtonElement} */ (event.target);
    const scope = (this.dataset.scope && this.closest(this.dataset.scope)) || this.parentElement;
    if (!scope) return;

    /** @type {Record<string, string>} */
    let values;
    try {
      values = JSON.parse(chip.dataset.values ?? '{}');
    } catch {
      return;
    }

    const wasPressed = chip.getAttribute('aria-pressed') === 'true';
    if (wasPressed && !this.hasAttribute('data-toggle')) return;
    const clearing = wasPressed;

    /** @type {HTMLInputElement | HTMLSelectElement | null} */
    let lastField = null;
    for (const [name, value] of Object.entries(values)) {
      const field = /** @type {HTMLInputElement | HTMLSelectElement | null} */ (
        scope.querySelector(`[name="${name}"]`)
      );
      if (!field) continue;
      field.value = clearing ? '' : value;
      lastField = field;
    }
    if (!lastField) return;

    for (const other of this.querySelectorAll('[data-values]')) {
      other.setAttribute('aria-pressed', String(!clearing && other === chip));
    }

    // Same event as the shopper's own input; bubbles to Horizon's on:change
    lastField.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('q-presets')) {
  customElements.define('q-presets', Presets);
}
